<script lang="ts">
  import type { SettlementDetails } from "../types.ts";
  import CheckCircle from "~icons/ph/check-circle-bold";
  import Caret from "~icons/ph/caret-down";
  import DownloadLink from "../../Settlement/elements/DownloadLink.svelte";
  import FooterButtons from "../../../components/workflow/FooterButtons.svelte";
  import ContinueButton from "../../../components/workflow/ContinueButton.svelte";
  import Dialog from "../../../components/Dialog.svelte";
  import {
    FaqAfterAccept,
    FaqNotAccept,
    FaqChangeDecision,
    FaqCounterOffer,
    FaqLessMoney,
    FaqAcceptOffer,
  } from "../faqs/Faq";

  let {
    settlementDetails,
    jlgMainPhone,
    errors = null,
  }: {
    settlementDetails: SettlementDetails;
    jlgMainPhone: string;
    errors?: { [key: string]: string } | null;
  } = $props();

  let choice = $state<"objected" | "accepted" | null>(null);
  let acceptDialog = $state<Dialog>();

  const continueButtonType = $derived(choice === "objected" ? "submit" : "button");

  function confirmAccept() {
    acceptDialog?.showModal();
    return false;
  }
</script>

{#if settlementDetails.jlgAdvice === "accept"}
  <h2 class="ui-title">We recommend accepting the offer</h2>
  <p class="mb-2">Here’s why:</p>
  <ul class="icon-list">
    <li><CheckCircle />Based on the facts of your claim, this is a strong offer</li>
    <li><CheckCircle />This is likely the highest you’ll receive</li>
    <li><CheckCircle />Accepting now means faster payment and certainty</li>
  </ul>
{:else if settlementDetails.jlgAdvice === "object"}
  <h2 class="ui-title">We recommend letting us seek a higher offer</h2>
  <fieldset class="ui-fieldset">
    <legend>How would you like to proceed?</legend>

    <div class="ui-option-container">
      <label class="flex flex-row gap-2 items-start">
        <input
          type="radio"
          name="settlement_financial[offer_status]"
          value="objected"
          class="radio"
          bind:group={choice}
          required />
        <span><b>Object</b> to the offer so JLG can counter for a better one</span>
      </label>

      <details class="ui-details mt-4">
        <summary><Caret />Why we recommend this</summary>
        <ul class="list-disc pl-6 space-y-2 mb-4">
          <li>We believe your claim is worth more</li>
          <li>We’ll handle the counter on your behalf</li>
          <li>Countering doesn’t put your current offer at risk</li>
        </ul>
      </details>
    </div>

    <div class="ui-option-container">
      <label class="flex flex-row gap-2 items-start">
        <!-- `value` for choice binding only; offer isn't accepted until ConfirmFees (button type, no submit here) -->
        <input
          type="radio"
          name="settlement_financial[offer_status]"
          value="accepted"
          class="radio"
          bind:group={choice}
          required />
        <span><b>Accept</b> the offer anyway</span>
      </label>
    </div>

    {#if errors}<div class="ui-error">{errors.general}</div>{/if}
  </fieldset>
{/if}

<p>For more information, you can download our letter of advice below.</p>

<h3 class="ui-section-title mb-4">Have questions?</h3>

<div class="ui-accordion">
  {#if settlementDetails.jlgAdvice === "accept"}
    <FaqAfterAccept />
    <FaqNotAccept phone={jlgMainPhone} />
    <FaqChangeDecision />
  {:else if settlementDetails.jlgAdvice === "object"}
    <FaqCounterOffer />
    <FaqLessMoney />
    <FaqAcceptOffer />
    <FaqChangeDecision />
  {/if}
</div>

<DownloadLink label="Download letter of advice" href={settlementDetails.letterOfAdviceUrl} />

{#if errors?.offer_status}
  <div class="ui-error">{errors.offer_status}</div>
{/if}

<FooterButtons>
  {#if settlementDetails.jlgAdvice === "accept"}
    <ContinueButton type="button" />
  {:else if settlementDetails.jlgAdvice === "object"}
    <ContinueButton
      label="Confirm response"
      type={continueButtonType}
      onclick={choice === "accepted" ? confirmAccept : undefined} />
  {/if}
</FooterButtons>

<Dialog bind:this={acceptDialog} showClose position="middle" class="w-[92%] rounded-lg" animate={false}>
  <h2 class="ui-section-title -mt-[0.4rem] pr-16">Are you sure you want to accept the offer?</h2>
  <p>
    We highly recommend letting us seek a higher offer. If you accept on the next screen, you won’t be able to change
    your decision later.
  </p>
  <div class="floating-footer mt-0">
    <button class="btn btn-ghost" type="button" onclick={() => acceptDialog?.close()}>Back</button>
    <ContinueButton type="button" label="Continue" class="text-white btn-error" />
  </div>
</Dialog>

<style lang="postcss">
  .icon-list {
    & > li {
      @apply flex items-start gap-1 mb-2;
    }

    :global(svg) {
      @apply text-emerald-500 h-6 w-6 shrink-0;
    }
  }
</style>
